import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Clock, Flame, Sparkles, Loader2, X } from 'lucide-react';
import { useSession } from '../lib/session.jsx';
import { useCrystal } from '../lib/crystalState.jsx';
import { fields } from '../lib/api.js';
import { PLANS } from '../lib/config.js';
import { STAGES } from '../lib/crystal.js';
import ProtectionDial from '../components/ProtectionDial.jsx';
import Crystal from '../components/Crystal.jsx';
import { Glass, Heading, Tag, Empty, Button, Notice, Stat } from '../components/ui.jsx';

export default function Dashboard() {
  const { profile, subscription, hasActiveSubscription, device } = useSession();
  const [mine, setMine] = useState([]);

  useEffect(() => { fields.mine().then(setMine).catch(console.error); }, []);
  const live = mine.filter((f) => f.status === 'active' || f.status === 'building');

  const first = profile?.full_name?.split(' ')[0];
  const plan = subscription ? PLANS[subscription.tier] : null;

  return (
    <div>
      <header className="rise rise-1 pt-2 pb-6 md:pb-8">
        <p className="eyebrow">{greeting()}</p>
        <h1 className="display text-[2.6rem] md:text-[3.4rem] mt-2">
          {first ? <>Welcome back, <span className="display-i lit">{first}</span>.</> : 'The line is open.'}
        </h1>
      </header>

      {(!hasActiveSubscription || (device && !device.is_active)) && (
        <div className="rise rise-2 mb-6 space-y-2">
          {!hasActiveSubscription && (
            <Notice to="/subscription" label="Choose a covenant" Link={Link} tone="warn">
              No active covenant — your shield is capped at 9%.
            </Notice>
          )}
          {device && !device.is_active && (
            <Notice to="/profile" label="Manage devices" Link={Link} tone="static">
              This device is dormant and draws no energy.
            </Notice>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4 md:gap-5">
        <CrystalPanel />

        <div className="space-y-4 md:space-y-5">
          <Glass className="rise rise-3 flex flex-col items-center py-7">
            <p className="eyebrow self-start -mt-2 mb-2">Your shield</p>
            <ProtectionDial size={220} />
          </Glass>

          <Glass className="rise rise-4">
            <Heading eyebrow="Your covenant" title={plan ? plan.name : 'Unbound'}
              action={<Link to="/subscription" className="text-xs font-semibold opacity-70 hover:opacity-100" style={{ color: 'var(--clay)' }}>Manage</Link>}
              className="mb-4" />
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Devices" value={subscription ? subscription.device_limit : '—'} />
              <Stat label="Monthly" value={subscription ? subscription.monthly_tokens : '—'} accent="gold" />
              <Stat label="Renews" value={subscription
                ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                : '—'} />
            </div>
          </Glass>
        </div>
      </div>

      <Glass className="rise rise-5 mt-4 md:mt-5">
        <Heading eyebrow="Consecrated ground" title="Your fields"
          action={<Link to="/map" className="btn btn-glass !py-2 !px-3.5 text-xs">Open map <ArrowUpRight size={13} /></Link>} />
        {live.length === 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Empty className="flex-1">No fields are burning. Energize the ground where you stand and every device inside it charges to full.</Empty>
            <Link to="/map" className="btn btn-mint shrink-0">Energize this area</Link>
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--edge)' }}>
            {live.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 py-3.5 first:pt-0 last:pb-0"
                  style={{ borderColor: 'var(--edge)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                        style={{ background: 'color-mix(in oklab, var(--sage) 16%, transparent)', color: 'var(--sage)' }}>
                    <Flame size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold capitalize leading-tight">{f.intensity}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{f.radius_m}m · {f.is_public ? 'Public' : 'Private'}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <Tag tone={f.status === 'building' ? 'building' : 'active'} dot>{f.status}</Tag>
                  <p className="text-[0.7rem] mt-1.5 flex items-center gap-1 justify-end" style={{ color: 'var(--text-3)' }}>
                    <Clock size={10} />
                    {f.status === 'building' ? 'Forming'
                      : new Date(f.expires_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Glass>

      <div className="rise rise-6 mt-4 md:mt-5 grid sm:grid-cols-2 gap-4 md:gap-5">
        <Tile to="/ceremonies" eyebrow="Broadcast" title="Ceremonies" body="Full-moon cleansings, equinox alignments and blessings from the server." />
        <Tile to="/map" eyebrow="Nearby" title="Healing map" body="Public fields near you, and the ground you have consecrated." />
      </div>
    </div>
  );
}

function CrystalPanel() {
  const { info, hue, seed, state, loaded, attune, burst, leveledTo, clearLevelUp } = useCrystal();
  const { device, hasActiveSubscription } = useSession();
  const [busy, setBusy] = useState(false);
  const charging = Boolean(device?.checked_in_field_id) && hasActiveSubscription;

  const doAttune = async () => {
    setBusy(true);
    try { await attune(); } catch (e) { console.error(e); } finally { setBusy(false); }
  };

  return (
    <Glass className="rise rise-2 relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: `radial-gradient(60% 50% at 50% 55%, hsl(${hue} 40% 70% / 0.18), transparent 70%)` }} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="eyebrow">Your crystal</p>
          <h2 className="display text-[2rem] mt-1">{loaded ? info.name : '…'}</h2>
        </div>
        {loaded && state.streak > 0 && (
          <Tag tone="active" dot>{state.streak}-day streak</Tag>
        )}
      </div>

      <div className="relative flex justify-center -my-2 md:-my-4">
        {loaded
          ? <Crystal xp={state.xp} hue={hue} seed={seed} size={320} charging={charging} burst={burst} interactive />
          : <div className="h-[320px]" />}
      </div>

      {loaded && (
        <div className="relative">
          <div className="flex items-end justify-between mb-2">
            <p className="text-sm" style={{ color: 'var(--text-2)' }}>
              <span className="font-semibold" style={{ color: 'var(--text)' }}>Level {info.level}</span>
              {info.next ? <> · {info.xpToNext} to {info.next}</> : ' · fully grown'}
            </p>
            <p className="text-xs nums" style={{ color: 'var(--text-3)' }}>{state.xp} xp</p>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--glass-2)' }}>
            <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${info.progress * 100}%` }}
                        transition={{ type: 'spring', stiffness: 60, damping: 20 }}
                        style={{ background: `hsl(${hue} 45% 58%)` }} />
          </div>
          <p className="display-i text-base mt-3" style={{ color: 'var(--text-2)' }}>{info.blurb}</p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {state.attuned_today ? (
              <span className="btn btn-outline cursor-default" style={{ color: 'var(--sage)' }}>
                <Sparkles size={15} /> Attuned today
              </span>
            ) : (
              <Button variant="gold" onClick={doAttune} disabled={busy}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Attune today · +15
              </Button>
            )}
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>
              {state.attunements} attunement{state.attunements === 1 ? '' : 's'} · {state.moons_held} month{state.moons_held === 1 ? '' : 's'} held · {state.tokens_given} tokens given
            </span>
          </div>
        </div>
      )}

      <AnimatePresence>
        {leveledTo && (
          <motion.div key="lvl" initial={{ opacity: 0, y: 12, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="relative mt-5 glass-flat p-4 flex items-center gap-3"
                      style={{ borderColor: `hsl(${hue} 45% 58%)` }}>
            <Sparkles size={18} style={{ color: `hsl(${hue} 45% 50%)` }} />
            <div className="flex-1">
              <p className="font-semibold">Your crystal grew.</p>
              <p className="text-sm" style={{ color: 'var(--text-2)' }}>It is now a {STAGES[leveledTo - 1].name}. {STAGES[leveledTo - 1].blurb}</p>
            </div>
            <button onClick={clearLevelUp} className="p-1.5 opacity-60 hover:opacity-100"><X size={15} /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </Glass>
  );
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 5 ? 'Deep night' : h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';
};

function Tile({ to, eyebrow, title, body }) {
  return (
    <Link to={to} className="glass glass-hover p-6 flex items-start justify-between gap-4">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <p className="display text-[1.7rem] mt-1.5">{title}</p>
        <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>{body}</p>
      </div>
      <span className="h-9 w-9 rounded-full grid place-items-center shrink-0"
            style={{ background: 'var(--glass-2)', color: 'var(--clay)' }}>
        <ArrowUpRight size={16} />
      </span>
    </Link>
  );
}
